/* eslint-disable no-nested-ternary */
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  styled
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { EconsultUtils } from '@oh-vcp/components-common';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OhipValidationService, RequiredFieldTypography } from '@oh-vcp/components-ui';

import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import ValidatorUtils from '../econsultBaseValidator/EconsultBaseValidator';
import { ValidationTypes } from '../econsultBaseValidator/EconsultValidationTypes';
import { ValidationResult } from '../econsultBaseValidator/ValidationResult';

const PatientFormTextField = styled(TextField)({
  width: '70%',
  '& .MuiInputBase-input': {
    fontSize: '0.875rem'
  },
  '& .MuiInputBase-input::placeholder': {
    fontSize: '0.875rem'
  }
});

const PatientFormTextFieldFull = styled(TextField)({
  '& .MuiInputBase-input': {
    fontSize: '0.875rem'
  },
  '& .MuiInputBase-input::placeholder': {
    fontSize: '0.875rem'
  }
});

const PatientFormDateField = styled(DesktopDatePicker)({
  '& .MuiInputBase-input': {
    height: 10,
    fontSize: '0.875rem'
  },
  '& .MuiInputBase-input::placeholder': {
    fontSize: '0.875rem'
  }
});

const vaildFieldMessageType = {
  display: 'none',
  fontSize: '0.75rem'
};
const inVaildFieldMessageType = {
  display: 'block',
  color: 'red',
  fontSize: '0.75rem'
};

const EconsultCasePatientForm = forwardRef((props, ref) => {
  const { caseDetails, setCaseDetails } = useEconsultCaseDetailsContext();
  const [isOhipValid, setIsOhipValid] = useState<boolean | undefined>(undefined);
  const [noOhip, setNoOhip] = useState<boolean>(Boolean(caseDetails?.patient?.noOhip));
  const [isOhipFieldDirty, setIsOhipFieldDirty] = useState<boolean>(false);
  const [isConsentShown, setIsConsentShown] = useState<boolean>(
    Boolean(caseDetails?.patientConsent)
  );
  const [patientConsentDetails, setPatientConsentDetails] = useState<string | undefined>(
    caseDetails?.patientConsent
  );
  const requiredFields: Map<string, ValidationTypes> = new Map();
  requiredFields.set('firstName', ValidationTypes.Text);
  requiredFields.set('lastName', ValidationTypes.Text);
  requiredFields.set('dateOfBirth', ValidationTypes.Date);
  requiredFields.set('gender', ValidationTypes.Text);
  const [dirtyFields, setFieldDiryty] = useState<string[]>([]);
  const [invalidFields, setFieldInvalid] = useState<string[]>([]);
  const { t } = useTranslation();
  const [invalidOhipMessage, setInvalidOhipMessage] = useState(t('Case.patient.ohip.required'));
  const [isPatientValid, setIsPatientValid] = useState(true);
  const [dobOutOfRange, setDobOutOfRange] = useState<boolean>(false);
  const [dobValid, setDobValid] = useState<boolean>(true);
  const unSelectedOptFontColor = 'base.grey4';
  const selectedOptFontColor = 'primary.main';
  const [consentFontColor, setConsentFontColor] = useState(unSelectedOptFontColor);
  const [ohipFontColor, setOhipFontColor] = useState(unSelectedOptFontColor);
  const [maleFontColor, setMaleFontColor] = useState(unSelectedOptFontColor);
  const [femaleFontColor, setFemaleFontColor] = useState(unSelectedOptFontColor);
  const [otherFontColor, setOtherFontColor] = useState(unSelectedOptFontColor);
  const [genderErrorMessageStyle, setGenderErrorMessageStyle] = useState(vaildFieldMessageType);
  const [consentErrorMessageStyle, setConsentErrorMessageStyle] = useState(vaildFieldMessageType);
  const currentDate = () => {
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const date = today.getDate();
    return `${year}-${month}-${date}`;
  };

  const isPatientFormValid = (): boolean => {
    let isPatientDetailsValid: boolean = true;
    if (!caseDetails?.patient?.firstName) {
      setFieldInvalid(ValidatorUtils.addToList(invalidFields, 'firstName'));
      setFieldDiryty(ValidatorUtils.addToList(dirtyFields, 'firstName'));
      isPatientDetailsValid = false;
    }
    if (!caseDetails?.patient?.lastName) {
      setFieldInvalid(ValidatorUtils.addToList(invalidFields, 'lastName'));
      setFieldDiryty(ValidatorUtils.addToList(dirtyFields, 'lastName'));
      isPatientDetailsValid = false;
    }
    if (!caseDetails?.patient?.gender) {
      setGenderErrorMessageStyle(inVaildFieldMessageType);
      setFieldDiryty(ValidatorUtils.addToList(dirtyFields, 'gender'));
      isPatientDetailsValid = false;
    }
    if (
      !caseDetails?.patient?.dateOfBirth ||
      dayjs(caseDetails?.patient?.dateOfBirth) > dayjs(currentDate())
    ) {
      setFieldInvalid(ValidatorUtils.addToList(invalidFields, 'dateOfBirth'));
      setFieldDiryty(ValidatorUtils.addToList(dirtyFields, 'dateOfBirth'));
      isPatientDetailsValid = false;
      if (dayjs(caseDetails?.patient?.dateOfBirth) > dayjs(currentDate())) {
        setDobOutOfRange(true);
      } else {
        setDobOutOfRange(false);
      }
    } else {
      setDobOutOfRange(false);
    }
    if (
      caseDetails?.patient?.dateOfBirth &&
      caseDetails?.patient?.dateOfBirth !== 'Invalid Date' &&
      dayjs(caseDetails?.patient?.dateOfBirth).year() >= 1900
    ) {
      setDobValid(EconsultUtils.dateValidate(caseDetails?.patient?.dateOfBirth));
    } else {
      isPatientDetailsValid = false;
      setFieldInvalid(ValidatorUtils.addToList(invalidFields, 'dateOfBirth'));
      setFieldDiryty(ValidatorUtils.addToList(dirtyFields, 'dateOfBirth'));
      setDobValid(false);
    }
    if (
      !caseDetails?.patient?.noOhip &&
      !OhipValidationService.isOhipNumberValid(caseDetails?.patient?.ohipNumber)
    ) {
      setFieldInvalid(ValidatorUtils.addToList(invalidFields, 'ohipNumber'));
      setIsOhipValid(false);
      setIsOhipFieldDirty(true);
      isPatientDetailsValid = false;
      if (!caseDetails?.patient?.ohipNumber) {
        setInvalidOhipMessage(t('Case.patient.ohip.required'));
      } else {
        setInvalidOhipMessage(t('Case.patient.ohip.invalid'));
      }
    }

    if (isConsentShown && !patientConsentDetails) {
      setConsentErrorMessageStyle(inVaildFieldMessageType);
      setFieldInvalid(ValidatorUtils.addToList(invalidFields, 'patientConsent'));
      setFieldDiryty(ValidatorUtils.addToList(dirtyFields, 'patientConsent'));
      isPatientDetailsValid = false;
    } else {
      setConsentErrorMessageStyle(vaildFieldMessageType);
    }
    setFieldInvalid(invalidFields);
    setIsPatientValid(isPatientDetailsValid);
    return isPatientDetailsValid;
  };
  useImperativeHandle(ref, () => ({
    validate: () => {
      return isPatientFormValid();
    }
  }));
  const [dob, setDob] = React.useState<Dayjs | null>(dayjs(''));

  const handleGenderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMaleFontColor(unSelectedOptFontColor);
    setFemaleFontColor(unSelectedOptFontColor);
    setOtherFontColor(unSelectedOptFontColor);
    const { value } = event.target;
    if (value === 'M') {
      setMaleFontColor(selectedOptFontColor);
    } else if (value === 'F') {
      setFemaleFontColor(selectedOptFontColor);
    } else if (value === 'O') {
      setOtherFontColor(selectedOptFontColor);
    }
    if (value) {
      setGenderErrorMessageStyle(vaildFieldMessageType);
    }
  };

  const handleGenderLoad = (gender: string) => {
    setMaleFontColor(unSelectedOptFontColor);
    setFemaleFontColor(unSelectedOptFontColor);
    setOtherFontColor(unSelectedOptFontColor);

    if (gender === 'M') {
      setMaleFontColor(selectedOptFontColor);
    } else if (gender === 'F') {
      setFemaleFontColor(selectedOptFontColor);
    } else if (gender === 'O') {
      setOtherFontColor(selectedOptFontColor);
    }
  };
  const handleNoOhiplLoad = (noOhipVal: boolean) => {
    setOhipFontColor(unSelectedOptFontColor);
    if (noOhipVal) {
      setOhipFontColor(selectedOptFontColor);
    }
  };
  const handleConsentGivenLoad = (consentGiven: boolean) => {
    setConsentFontColor(unSelectedOptFontColor);
    if (consentGiven) {
      setConsentFontColor(selectedOptFontColor);
      setIsConsentShown(true);
      setPatientConsentDetails(caseDetails?.patientConsent);
    }
  };
  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;
    const fieldName = { name }.name;

    handleGenderChange(event);

    const res: ValidationResult = ValidatorUtils.handleValidation(
      requiredFields,
      dirtyFields,
      invalidFields,
      fieldName,
      value
    );
    setIsPatientValid(res.isValid);
    setFieldDiryty(res.dirtyFields);
    setFieldInvalid(res.invalidFields);
    const updatedCaseDetails = {
      ...caseDetails,
      patient: {
        ...caseDetails?.patient,
        [name]: value
      }
    };
    setCaseDetails(updatedCaseDetails);
  };
  const validateOhipVersion = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const updatedCaseDetails = {
      ...caseDetails,
      patient: {
        ...caseDetails?.patient,
        ohipVersion: value
      }
    };
    setCaseDetails(updatedCaseDetails);
  };
  const handleValidateOhip = (value: string, invalidOhipMessageForLength: string) => {
    if (!OhipValidationService.isOhipNumberLenghtValid(value)) {
      setInvalidOhipMessage(invalidOhipMessageForLength);
      setIsOhipValid(false);
    } else if (!OhipValidationService.isOhipNumberValid(value)) {
      setInvalidOhipMessage(t('Case.patient.ohip.invalid'));
      setIsOhipValid(false);
    } else {
      setIsOhipValid(true);
    }

    const noohip = value === '';

    const updatedCaseDetails = {
      ...caseDetails,
      patient: {
        ...caseDetails?.patient,
        ohipNumber: value,
        noOhip: noohip
      }
    };
    setCaseDetails(updatedCaseDetails);
  };
  const validateOhip = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isOhipFieldDirty) {
      setIsOhipFieldDirty(true);
    }
    const { value } = event.target;
    handleValidateOhip(value, '');

    handleFieldChange(event);
    setNoOhip(false);
  };
  const validateOhipOnFocusOut: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (
    event
  ) => {
    if (caseDetails?.patient?.noOhip) return;
    if (!isOhipFieldDirty) {
      setIsOhipFieldDirty(true);
    }
    const { value } = event.target;
    if (!value) {
      setInvalidOhipMessage(t('Case.patient.ohip.required'));
    } else {
      handleValidateOhip(value, t('Case.patient.ohip.invalid'));
    }
  };
  const handleNoOhip = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOhipFieldDirty(false);
    setOhipFontColor(unSelectedOptFontColor);
    const updatedCaseDetails = {
      ...caseDetails,
      patient: {
        ...caseDetails?.patient,
        noOhip: event.target.checked
      }
    };
    if (event.target.checked) {
      setOhipFontColor(selectedOptFontColor);
      delete updatedCaseDetails.patient.ohipNumber;
      delete updatedCaseDetails.patient.ohipVersion;
    }

    setNoOhip(event.target.checked);

    setCaseDetails(updatedCaseDetails);
  };
  const handleConsentChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsConsentShown(event.target.checked);
    setConsentFontColor(event.target.checked ? selectedOptFontColor : unSelectedOptFontColor);
    if (caseDetails) {
      const { patientConsent, ...rest } = caseDetails;
      setCaseDetails(rest);
    }
    setPatientConsentDetails('');
  };
  const handlePatientConsentFieldChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setPatientConsentDetails(value);
    setCaseDetails({
      ...caseDetails,
      patientConsent: value
    });
  };
  const handlePatientConsentFieldBlur = () => {
    setCaseDetails({
      ...caseDetails,
      patientConsent: patientConsentDetails
    });
  };
  useEffect(() => {
    if (caseDetails && caseDetails.patient) {
      const nextDob = caseDetails.patient.dateOfBirth
        ? dayjs(caseDetails.patient.dateOfBirth)
        : dayjs('');
      setDob((prev) => {
        const prevValue = prev?.isValid() ? prev.format('YYYY-MM-DD') : '';
        const nextValue = nextDob.isValid() ? nextDob.format('YYYY-MM-DD') : '';
        return prevValue === nextValue ? prev : nextDob;
      });
      if (caseDetails.patient.gender) {
        handleGenderLoad(caseDetails.patient.gender);
      }
      if (caseDetails.patient.noOhip) {
        handleNoOhiplLoad(caseDetails.patient.noOhip);
      }
      if (caseDetails?.patientConsent) {
        handleConsentGivenLoad(true);
      }
    }
  }, [
    caseDetails?.patient?.dateOfBirth,
    caseDetails?.patient?.gender,
    caseDetails?.patient?.noOhip,
    caseDetails?.patientConsent
  ]);

  const handleDateOfBirthBlur = () => {
    if (dob && dob > dayjs(currentDate())) {
      setFieldInvalid(ValidatorUtils.addToList(invalidFields, 'dateOfBirth'));
      setFieldDiryty(ValidatorUtils.addToList(dirtyFields, 'dateOfBirth'));
      setDobOutOfRange(true);
    }
    if (!dob) {
      setDobValid(false);
    }
  };

  return (
    <Grid container spacing={0} rowSpacing={2}>
      <Grid item xs={12} md={3}>
        <RequiredFieldTypography variant="body2" color="base.grey4">
          {t('Case.patient.firstName')}
        </RequiredFieldTypography>
      </Grid>
      <Grid item xs={12} md={9}>
        <PatientFormTextField
          value={caseDetails?.patient?.firstName}
          name="firstName"
          id="firstName"
          placeholder={t('Case.patient.enterFirstName') as string | undefined}
          variant="standard"
          onChange={handleFieldChange}
          inputProps={{ maxLength: 50 }}
          // eslint-disable-next-line prettier/prettier
          error={ValidatorUtils.isFieldNotValidAndDirty(dirtyFields, invalidFields, 'firstName')}
          helperText={
            // eslint-disable-next-line prettier/prettier
            ValidatorUtils.isFieldNotValidAndDirty(dirtyFields, invalidFields, 'firstName')
              ? t('Case.patient.field.required')
              : ''
          }
        />
      </Grid>
      <Grid item xs={12} md={3} pl={1.5}>
        <Typography variant="body2" color="base.grey4">
          {t('Case.patient.middleName')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={9}>
        <PatientFormTextField
          value={caseDetails?.patient?.middleName}
          name="middleName"
          placeholder={t('Case.patient.enterMiddleName') as string | undefined}
          variant="standard"
          onChange={handleFieldChange}
          inputProps={{ maxLength: 50 }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <RequiredFieldTypography variant="body2" color="base.grey4">
          {t('Case.patient.lastName')}
        </RequiredFieldTypography>
      </Grid>
      <Grid item xs={12} md={9}>
        <PatientFormTextField
          value={caseDetails?.patient?.lastName}
          name="lastName"
          id="lastName"
          placeholder={t('Case.patient.enterLastName') as string | undefined}
          variant="standard"
          onChange={handleFieldChange}
          inputProps={{ maxLength: 50 }}
          // eslint-disable-next-line prettier/prettier
          error={ValidatorUtils.isFieldNotValidAndDirty(dirtyFields, invalidFields, 'lastName')}
          helperText={
            // eslint-disable-next-line prettier/prettier
            ValidatorUtils.isFieldNotValidAndDirty(dirtyFields, invalidFields, 'lastName')
              ? t('Case.patient.field.required')
              : ''
          }
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <RequiredFieldTypography variant="body2" color="base.grey4" pt={1}>
          {t('Case.patient.dob')}
        </RequiredFieldTypography>
      </Grid>
      <Grid item xs={12} md={9}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <PatientFormDateField
            format="YYYY-MM-DD"
            value={dob}
            onChange={(newValue) => {
              const fieldName = 'dateOfBirth';
              const updatedCaseDetails = {
                ...caseDetails,
                patient: {
                  ...caseDetails?.patient,
                  dateOfBirth: newValue?.format('YYYY-MM-DD') ?? ''
                }
              };
              setCaseDetails(updatedCaseDetails);
              if (ValidatorUtils.isValidField(newValue, ValidationTypes.Date)) {
                setFieldDiryty(ValidatorUtils.addToList(dirtyFields, fieldName));
                setFieldInvalid(ValidatorUtils.removeFromList(invalidFields, fieldName));
              }
              return setDob(newValue);
            }}
            name="dateOfBirth"
            maxDate={dayjs(currentDate())}
            slotProps={{
              textField: {
                onBlur: handleDateOfBirthBlur,
                helperText:
                  // eslint-disable-next-line prettier/prettier
                  (!isPatientValid || dobOutOfRange) &&
                  ValidatorUtils.isFieldNotValid(invalidFields, 'dateOfBirth')
                    ? !dobValid
                      ? dayjs(caseDetails?.patient?.dateOfBirth).year() < 1900
                        ? t('Case.patient.dobInvalidYear')
                        : t('Case.patient.dobInvalid')
                      : dobOutOfRange
                      ? t('Case.patient.dobOutOfRange')
                      : t('Case.patient.field.required')
                    : '',
                error:
                  (!isPatientValid || dobOutOfRange || !dobValid) &&
                  ValidatorUtils.isFieldNotValid(invalidFields, 'dateOfBirth')
              }
            }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={3}>
        <RequiredFieldTypography variant="body2" color="base.grey4" pt={1}>
          {t('Case.patient.gender')}
        </RequiredFieldTypography>
      </Grid>
      <Grid item xs={12} md={9}>
        <FormControl component="fieldset">
          <RadioGroup
            value={caseDetails?.patient?.gender}
            aria-label="gender"
            name="gender"
            onChange={handleFieldChange}>
            <Stack direction="row">
              <FormControlLabel
                value="M"
                control={
                  <Radio
                    sx={{
                      '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                        color: maleFontColor
                      },
                      '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                        color: maleFontColor
                      }
                    }}
                  />
                }
                label="Male"
                sx={{
                  color: maleFontColor,
                  fontSize: '0.875rem',
                  '& .MuiTypography-root': {
                    fontSize: '0.875rem'
                  }
                }}
              />
              <FormControlLabel
                value="F"
                control={
                  <Radio
                    sx={{
                      '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                        color: femaleFontColor
                      },
                      '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                        color: femaleFontColor
                      }
                    }}
                  />
                }
                label="Female"
                sx={{
                  color: femaleFontColor,
                  fontSize: '0.875rem',
                  '& .MuiTypography-root': {
                    fontSize: '0.875rem'
                  }
                }}
              />
              <FormControlLabel
                value="O"
                control={
                  <Radio
                    sx={{
                      '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                        color: otherFontColor
                      },
                      '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                        color: otherFontColor
                      }
                    }}
                  />
                }
                label="Other"
                sx={{
                  color: otherFontColor,
                  fontSize: '0.875rem',
                  '& .MuiTypography-root': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            </Stack>
          </RadioGroup>
        </FormControl>
        <Typography id="requestNteValidationdMessage" style={genderErrorMessageStyle}>
          {t('Case.patient.field.required')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={3}>
        <RequiredFieldTypography variant="body2" color="base.grey4" pt={1}>
          {t('Case.patient.ohip')}
        </RequiredFieldTypography>
      </Grid>
      <Grid item xs={12} md={9}>
        <Stack direction="column">
          <Stack direction="row" spacing={2}>
            <PatientFormTextFieldFull
              placeholder={t('Case.patient.enterOhip') as string | undefined}
              variant="standard"
              name="ohipNumber"
              inputProps={{ maxLength: 10 }}
              value={caseDetails?.patient?.ohipNumber ?? ''}
              onChange={validateOhip}
              onBlur={validateOhipOnFocusOut}
              error={!isOhipValid && isOhipFieldDirty}
              helperText={!isOhipValid && isOhipFieldDirty ? invalidOhipMessage : ''}
            />
            <PatientFormTextFieldFull
              placeholder={t('Case.patient.versionCode') as string | undefined}
              name="ohipVersion"
              value={caseDetails?.patient?.ohipVersion ?? ''}
              variant="standard"
              onChange={validateOhipVersion}
              inputProps={{ maxLength: 2 }}
            />
          </Stack>
          <FormGroup onChange={handleNoOhip}>
            <FormControlLabel
              name="noOhip"
              control={
                <Checkbox
                  checked={Boolean(noOhip)}
                  sx={{
                    '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                      color: ohipFontColor
                    },
                    '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                      color: ohipFontColor
                    }
                  }}
                />
              }
              label={t('Case.patient.ohipNotAvailable')}
              sx={{
                color: ohipFontColor,
                fontSize: '0.875rem',
                '& .MuiTypography-root': {
                  fontSize: '0.875rem'
                }
              }}
            />
          </FormGroup>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <FormGroup onChange={handleConsentChanged}>
          <FormControlLabel
            checked={isConsentShown}
            control={
              <Checkbox
                sx={{
                  '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                    color: consentFontColor
                  },
                  '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                    color: consentFontColor
                  }
                }}
              />
            }
            label={t('Case.patient.consentDirectives')}
            sx={{
              color: consentFontColor,
              fontSize: '0.875rem',
              '& .MuiTypography-root': {
                fontSize: '0.875rem'
              }
            }}
          />
        </FormGroup>
      </Grid>
      {isConsentShown && (
        <Grid item xs={12}>
          <PatientFormTextFieldFull
            name="patientConsent"
            value={patientConsentDetails}
            onChange={handlePatientConsentFieldChanged}
            onBlur={handlePatientConsentFieldBlur}
            fullWidth
            multiline
            rows={4}
            InputLabelProps={{
              shrink: true
            }}
            placeholder={
              // eslint-disable-next-line max-len
              'Please enter any consent limitations. Examples could include:\n1. Do not forward this consult to *name of person|Organization*\n2. Only send referral to *name of person | Organization* '
            }
          />
          <Typography id="requestNteValidationdMessage" style={consentErrorMessageStyle}>
            {t('Case.patient.field.required')}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
});

export default EconsultCasePatientForm;
